/**
 * Reconciliation Strategy - Pluggable conflict resolution for project content sync.
 *
 * Conflicts occur when both local and remote have changes since last confirmed sync.
 * Strategies are deterministic and explicit - no silent merges, no CRDTs.
 *
 * Available strategies:
 *   SequenceWinsStrategy  - higher sequence number wins (default)
 *   TimestampWinsStrategy - more recent savedAt timestamp wins
 *
 * NOT YET IMPLEMENTED (requires future collaboration infrastructure):
 *   ManualResolutionStrategy - surface conflict to user for explicit choice
 *   FieldMergeStrategy       - merge non-overlapping field changes
 */

import type { ProjectContentPayload } from "../storageAdapter";

export interface ReconciliationContext {
  projectId:    string;
  local:        ProjectContentPayload;
  remote:       ProjectContentPayload;
}

export type ReconciliationWinner = "local" | "remote";

export interface ReconciliationResult {
  winner:   ReconciliationWinner;
  payload:  ProjectContentPayload; // the winning payload to apply
  reason:   string;                // human-readable explanation
}

export interface ReconciliationStrategy {
  readonly id:  string;
  describe(): string;
  resolve(ctx: ReconciliationContext): ReconciliationResult;
}

/* =========================================================
   FACTORY
   ========================================================= */

const registry = new Map<string, ReconciliationStrategy>();

export function registerStrategy(strategy: ReconciliationStrategy): void {
  registry.set(strategy.id, strategy);
}

export function getStrategy(id: string): ReconciliationStrategy | undefined {
  return registry.get(id);
}

export function listStrategies(): ReconciliationStrategy[] {
  return [...registry.values()];
}
